import React, { useState } from 'react';
import {
    Stack,
    Text,
    DefaultButton,
    IconButton,
    Panel,
    PanelType,
    Dropdown,
    IDropdownOption,
    TextField,
    DatePicker,
    PrimaryButton,
    ChoiceGroup,
    IChoiceGroupOption,
    Checkbox,
    TooltipHost
} from '@fluentui/react';

interface ColumnSchemaInfo {
    columnName: string;
    dataType: string;
    isNullable: boolean;
    isPrimaryKey: boolean;
}

interface FilterDefinition {
    columnName: string;
    operator: number;
    value?: string;
    valueEnd?: string;
}

interface AggregationDefinition {
    columnName: string;
    function: number;
    alias?: string;
}

interface SortDefinition {
    columnName: string;
    direction: number;
}

interface ColumnListWithActionsProps {
    columns: ColumnSchemaInfo[];
    isAggregationMode: boolean;
    selectedColumns: string[];
    filters: FilterDefinition[];
    groupByColumns: string[];
    dateGroupings: Record<string, number>;
    aggregations: AggregationDefinition[];
    sortOrders: SortDefinition[];
    onSelectedColumnsChange: (columns: string[]) => void;
    onFiltersChange: (filters: FilterDefinition[]) => void;
    onGroupByColumnsChange: (columns: string[]) => void;
    onDateGroupingsChange: (groupings: Record<string, number>) => void;
    onAggregationsChange: (aggregations: AggregationDefinition[]) => void;
    onSortOrdersChange: (sortOrders: SortDefinition[]) => void;
}

enum FilterOperator {
    Equals = 0,
    NotEquals = 1,
    GreaterThan = 2,
    LessThan = 3,
    GreaterThanOrEqual = 4,
    LessThanOrEqual = 5,
    Between = 6,
    In = 7,
    Like = 8,
    IsNull = 9,
    IsNotNull = 10
}

enum AggregationFunction {
    Count = 0,
    CountDistinct = 1,
    Sum = 2,
    Avg = 3,
    Min = 4,
    Max = 5
}

enum SortDirection {
    Asc = 0,
    Desc = 1
}

enum DateGrouping {
    None = 0,
    Date = 1,
    Month = 2,
    Year = 3
}

const operatorOptions: IDropdownOption[] = [
    { key: FilterOperator.Equals, text: '=' },
    { key: FilterOperator.NotEquals, text: '<>' },
    { key: FilterOperator.GreaterThan, text: '>' },
    { key: FilterOperator.LessThan, text: '<' },
    { key: FilterOperator.GreaterThanOrEqual, text: '>=' },
    { key: FilterOperator.LessThanOrEqual, text: '<=' },
    { key: FilterOperator.Between, text: 'BETWEEN' },
    { key: FilterOperator.Like, text: 'LIKE' },
    { key: FilterOperator.IsNull, text: 'IS NULL' },
    { key: FilterOperator.IsNotNull, text: 'IS NOT NULL' }
];

const aggregationFunctionOptions: IDropdownOption[] = [
    { key: -1, text: 'None' },
    { key: AggregationFunction.Count, text: 'COUNT' },
    { key: AggregationFunction.CountDistinct, text: 'COUNT DISTINCT' },
    { key: AggregationFunction.Sum, text: 'SUM' },
    { key: AggregationFunction.Avg, text: 'AVG' },
    { key: AggregationFunction.Min, text: 'MIN' },
    { key: AggregationFunction.Max, text: 'MAX' }
];

const dateGroupingOptions: IChoiceGroupOption[] = [
    { key: String(DateGrouping.Date), text: 'Date' },
    { key: String(DateGrouping.Month), text: 'Month' },
    { key: String(DateGrouping.Year), text: 'Year' }
];

const ColumnListWithActions: React.FC<ColumnListWithActionsProps> = ({
    columns,
    isAggregationMode,
    selectedColumns,
    filters,
    groupByColumns,
    dateGroupings,
    aggregations,
    sortOrders,
    onSelectedColumnsChange,
    onFiltersChange,
    onGroupByColumnsChange,
    onDateGroupingsChange,
    onAggregationsChange,
    onSortOrdersChange
}) => {
    const [panelState, setPanelState] = useState<{
        isOpen: boolean;
        type: 'filter' | 'groupby' | 'aggregate' | null;
        columnName: string;
    }>({ isOpen: false, type: null, columnName: '' });

    const [tempFilter, setTempFilter] = useState<FilterDefinition>({
        columnName: '',
        operator: FilterOperator.Equals,
        value: '',
        valueEnd: ''
    });

    const [tempAggregation, setTempAggregation] = useState<{
        function: number;
    }>({ function: AggregationFunction.Count });

    const [tempDateGrouping, setTempDateGrouping] = useState<number>(DateGrouping.None);

    const isDateColumn = (columnName: string): boolean => {
        const column = columns.find(c => c.columnName === columnName);
        return column?.dataType.toLowerCase().includes('date') ||
            column?.dataType.toLowerCase().includes('time') || false;
    };

    const openFilterPanel = (columnName: string) => {
        const existingFilter = filters.find(f => f.columnName === columnName);
        setTempFilter(existingFilter || {
            columnName,
            operator: FilterOperator.Equals,
            value: '',
            valueEnd: ''
        });
        setPanelState({ isOpen: true, type: 'filter', columnName });
    };

    const handleGroupByClick = (columnName: string) => {
        const isDateCol = isDateColumn(columnName);
        const isCurrentlyGrouped = groupByColumns.includes(columnName);

        if (!isDateCol) {
            // Non-date columns: just toggle group by
            if (isCurrentlyGrouped) {
                onGroupByColumnsChange(groupByColumns.filter(c => c !== columnName));
            } else {
                // Remove any aggregations for this column before adding to GROUP BY
                const remainingAggregations = aggregations.filter(a => a.columnName !== columnName);
                if (remainingAggregations.length !== aggregations.length) {
                    onAggregationsChange(remainingAggregations);
                }
                onGroupByColumnsChange([...groupByColumns, columnName]);
            }
        } else {
            // Date columns: open panel to configure date grouping
            const defaultGrouping = DateGrouping.Date;
            setTempDateGrouping(dateGroupings[columnName] || defaultGrouping);
            setPanelState({ isOpen: true, type: 'groupby', columnName });
        }
    };

    const openAggregatePanel = (columnName: string) => {
        // Always start fresh - allowing multiple aggregations per column
        setTempAggregation({
            function: -1 // Start with None (-1) to prompt user to select
        });
        setPanelState({ isOpen: true, type: 'aggregate', columnName });
    };

    const closePanel = () => {
        setPanelState({ isOpen: false, type: null, columnName: '' });
    };

    const saveFilter = () => {
        const newFilters = filters.filter(f => f.columnName !== panelState.columnName);
        newFilters.push(tempFilter);
        onFiltersChange(newFilters);
        closePanel();
    };

    const removeFilter = () => {
        onFiltersChange(filters.filter(f => f.columnName !== panelState.columnName));
        closePanel();
    };

    const saveGroupBy = () => {
        if (!groupByColumns.includes(panelState.columnName)) {
            // Remove any aggregations for this column before adding to GROUP BY
            const remainingAggregations = aggregations.filter(a => a.columnName !== panelState.columnName);
            if (remainingAggregations.length !== aggregations.length) {
                onAggregationsChange(remainingAggregations);
            }
            onGroupByColumnsChange([...groupByColumns, panelState.columnName]);
        }
        if (isDateColumn(panelState.columnName)) {
            onDateGroupingsChange({ ...dateGroupings, [panelState.columnName]: tempDateGrouping });
        }
        closePanel();
    };

    const removeGroupBy = () => {
        onGroupByColumnsChange(groupByColumns.filter(c => c !== panelState.columnName));
        const newGroupings = { ...dateGroupings };
        delete newGroupings[panelState.columnName];
        onDateGroupingsChange(newGroupings);
        closePanel();
    };

    const handleAggregationChange = (functionKey: number) => {
        if (functionKey === -1) {
            // None selected - just close panel without adding
            closePanel();
        } else {
            // Function selected - add new aggregation (allows multiple per column)
            const funcName = aggregationFunctionOptions.find(o => o.key === functionKey)?.text || '';
            const newAgg: AggregationDefinition = {
                columnName: panelState.columnName,
                function: functionKey,
                alias: `${panelState.columnName}_${funcName}`
            };

            // Remove column from GROUP BY before adding aggregation
            if (groupByColumns.includes(panelState.columnName)) {
                onGroupByColumnsChange(groupByColumns.filter(c => c !== panelState.columnName));
                // Also remove any date grouping
                const newGroupings = { ...dateGroupings };
                delete newGroupings[panelState.columnName];
                onDateGroupingsChange(newGroupings);
            }

            // Don't filter out existing aggregations - just add the new one
            onAggregationsChange([...aggregations, newAgg]);
            closePanel();
        }
    };

    const removeAggregate = () => {
        onAggregationsChange(aggregations.filter(a => a.columnName !== panelState.columnName));
        closePanel();
    };

    const toggleColumnSelection = (columnName: string) => {
        if (selectedColumns.includes(columnName)) {
            onSelectedColumnsChange(selectedColumns.filter(c => c !== columnName));
        } else {
            onSelectedColumnsChange([...selectedColumns, columnName]);
        }
    };

    const getColumnStatus = (column: ColumnSchemaInfo) => {
        const badges: Array<{ text: string; type: 'filter' | 'groupby' | 'aggregate'; functionKey?: number }> = [];
        const filter = filters.find(f => f.columnName === column.columnName);
        if (filter) {
            const opText = operatorOptions.find(o => o.key === filter.operator)?.text;
            badges.push({ text: `Filter: ${opText} ${filter.value || ''}`, type: 'filter' });
        }
        if (groupByColumns.includes(column.columnName)) {
            const grouping = dateGroupings[column.columnName];
            const groupText = grouping ? dateGroupingOptions.find(o => o.key === String(grouping))?.text : '';
            badges.push({ text: `Group By${groupText ? ': ' + groupText : ''}`, type: 'groupby' });
        }
        // Show ALL aggregations for this column (not just the first one)
        const columnAggs = aggregations.filter(a => a.columnName === column.columnName);
        columnAggs.forEach(agg => {
            const funcText = aggregationFunctionOptions.find(o => o.key === agg.function)?.text;
            badges.push({ text: `Agg: ${funcText}`, type: 'aggregate', functionKey: agg.function });
        });
        return badges;
    };

    const quickRemove = (columnName: string, type: 'filter' | 'groupby' | 'aggregate', functionKey?: number) => {
        if (type === 'filter') {
            onFiltersChange(filters.filter(f => f.columnName !== columnName));
        } else if (type === 'groupby') {
            onGroupByColumnsChange(groupByColumns.filter(c => c !== columnName));
            const newGroupings = { ...dateGroupings };
            delete newGroupings[columnName];
            onDateGroupingsChange(newGroupings);
        } else if (type === 'aggregate') {
            // Remove specific aggregation by function key (allows multiple aggregations per column)
            if (functionKey !== undefined) {
                onAggregationsChange(aggregations.filter(a =>
                    !(a.columnName === columnName && a.function === functionKey)
                ));
            } else {
                // Fallback: remove all aggregations for this column
                onAggregationsChange(aggregations.filter(a => a.columnName !== columnName));
            }
        }
    };

    const handleSortClick = (columnName: string, direction: SortDirection) => {
        const existingSort = sortOrders.find(s => s.columnName === columnName);

        if (existingSort && existingSort.direction === direction) {
            // Clicking the same sort direction again removes the sort
            onSortOrdersChange(sortOrders.filter(s => s.columnName !== columnName));
        } else {
            // Remove any existing sort for this column and add new one
            const newSortOrders = sortOrders.filter(s => s.columnName !== columnName);
            newSortOrders.push({ columnName, direction });
            onSortOrdersChange(newSortOrders);
        }
    };

    const needsValue = (operator: number): boolean => {
        return operator !== FilterOperator.IsNull && operator !== FilterOperator.IsNotNull;
    };

    const needsEndValue = (operator: number): boolean => {
        return operator === FilterOperator.Between;
    };

    return (
        <>
            <Stack tokens={{ childrenGap: 15 }}>
                <Text variant="large" styles={{ root: { fontWeight: 600 } }}>
                    Columns
                </Text>

                {!isAggregationMode && (
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <DefaultButton
                            text="Select All"
                            onClick={() => onSelectedColumnsChange(columns.map(c => c.columnName))}
                        />
                        <DefaultButton
                            text="Clear Selection"
                            onClick={() => onSelectedColumnsChange([])}
                        />
                    </Stack>
                )}

                <Stack tokens={{ childrenGap: 5 }}>
                    {columns.map(column => {
                        const badges = getColumnStatus(column);
                        const isSelected = selectedColumns.includes(column.columnName);
                        const isGrouped = groupByColumns.includes(column.columnName);
                        const isAggregated = aggregations.some(a => a.columnName === column.columnName);
                        const sortOrder = sortOrders.find(s => s.columnName === column.columnName);
                        const isSortedAsc = sortOrder?.direction === SortDirection.Asc;
                        const isSortedDesc = sortOrder?.direction === SortDirection.Desc;

                        return (
                            <Stack
                                key={column.columnName}
                                horizontal
                                verticalAlign="center"
                                tokens={{ childrenGap: 10 }}
                                styles={{
                                    root: {
                                        padding: 10,
                                        border: '1px solid #edebe9',
                                        borderRadius: 2,
                                        backgroundColor: (isSelected || isGrouped || isAggregated) ? '#f3f2f1' : '#ffffff'
                                    }
                                }}
                            >
                                <Stack styles={{ root: { width: 200 } }}>
                                    <Text styles={{ root: { fontWeight: 600 } }}>
                                        {column.columnName}
                                        {column.isPrimaryKey && ' 🔑'}
                                    </Text>
                                    <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                                        {column.dataType}
                                    </Text>
                                </Stack>

                                <Stack horizontal tokens={{ childrenGap: 5 }}>
                                    <IconButton
                                        iconProps={{ iconName: 'SortUp' }}
                                        title="Sort Ascending"
                                        ariaLabel="Sort Ascending"
                                        onClick={() => handleSortClick(column.columnName, SortDirection.Asc)}
                                        styles={{
                                            root: isSortedAsc ? { backgroundColor: '#0078d4', color: 'white' } : {},
                                            icon: isSortedAsc ? { color: 'white' } : {}
                                        }}
                                    />
                                    <IconButton
                                        iconProps={{ iconName: 'SortDown' }}
                                        title="Sort Descending"
                                        ariaLabel="Sort Descending"
                                        onClick={() => handleSortClick(column.columnName, SortDirection.Desc)}
                                        styles={{
                                            root: isSortedDesc ? { backgroundColor: '#0078d4', color: 'white' } : {},
                                            icon: isSortedDesc ? { color: 'white' } : {}
                                        }}
                                    />
                                </Stack>

                                <Stack horizontal tokens={{ childrenGap: 5 }} grow>
                                    {!isAggregationMode && (
                                        <Checkbox
                                            label="Select"
                                            checked={isSelected}
                                            onChange={() => toggleColumnSelection(column.columnName)}
                                        />
                                    )}

                                    {isAggregationMode && (
                                        <>
                                            <TooltipHost
                                                content={isAggregated ? "Cannot group by - column has aggregations" : "Add to GROUP BY clause"}
                                            >
                                                <DefaultButton
                                                    text={isGrouped ? "✓ Group By" : "Group By"}
                                                    onClick={() => handleGroupByClick(column.columnName)}
                                                    disabled={isAggregated}
                                                    styles={{ root: isGrouped ? { backgroundColor: '#e1dfdd' } : {} }}
                                                />
                                            </TooltipHost>
                                            <TooltipHost
                                                content={isGrouped ? "Cannot aggregate - column is in GROUP BY" : "Add aggregation function"}
                                            >
                                                <DefaultButton
                                                    text={isAggregated ? "✓ Aggregate" : "Aggregate"}
                                                    onClick={() => openAggregatePanel(column.columnName)}
                                                    disabled={isGrouped}
                                                    styles={{ root: isAggregated ? { backgroundColor: '#e1dfdd' } : {} }}
                                                />
                                            </TooltipHost>
                                        </>
                                    )}

                                    <DefaultButton
                                        text={filters.some(f => f.columnName === column.columnName) ? "✓ Filter" : "Filter"}
                                        onClick={() => openFilterPanel(column.columnName)}
                                        styles={{ root: filters.some(f => f.columnName === column.columnName) ? { backgroundColor: '#e1dfdd' } : {} }}
                                    />
                                </Stack>

                                {badges.length > 0 && (
                                    <Stack tokens={{ childrenGap: 5 }}>
                                        {badges.map((badge, idx) => (
                                            <Stack key={idx} horizontal verticalAlign="center" tokens={{ childrenGap: 5 }} styles={{
                                                root: {
                                                    padding: '2px 8px',
                                                    backgroundColor: '#0078d4',
                                                    color: 'white',
                                                    borderRadius: 2,
                                                    fontSize: 11
                                                }
                                            }}>
                                                <Text variant="small" styles={{ root: { color: 'white' } }}>
                                                    {badge.text}
                                                </Text>
                                                <IconButton
                                                    iconProps={{ iconName: 'Cancel' }}
                                                    title="Remove"
                                                    ariaLabel="Remove"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        quickRemove(column.columnName, badge.type, badge.functionKey);
                                                    }}
                                                    styles={{
                                                        root: {
                                                            color: 'white',
                                                            width: 16,
                                                            height: 16
                                                        },
                                                        icon: { fontSize: 10 }
                                                    }}
                                                />
                                            </Stack>
                                        ))}
                                    </Stack>
                                )}
                            </Stack>
                        );
                    })}
                </Stack>
            </Stack>

            {/* Filter Panel */}
            <Panel
                isOpen={panelState.isOpen && panelState.type === 'filter'}
                onDismiss={closePanel}
                type={PanelType.medium}
                headerText={`Filter: ${panelState.columnName}`}
            >
                <Stack tokens={{ childrenGap: 15 }} styles={{ root: { marginTop: 20 } }}>
                    <Dropdown
                        label="Operator"
                        options={operatorOptions}
                        selectedKey={tempFilter.operator}
                        onChange={(_, option) => setTempFilter({ ...tempFilter, operator: option?.key as number })}
                    />

                    {needsValue(tempFilter.operator) && (
                        <>
                            {isDateColumn(panelState.columnName) ? (
                                <DatePicker
                                    label="Value"
                                    value={tempFilter.value ? new Date(tempFilter.value) : undefined}
                                    onSelectDate={(date) => setTempFilter({ ...tempFilter, value: date?.toISOString().split('T')[0] || '' })}
                                />
                            ) : (
                                <TextField
                                    label="Value"
                                    value={tempFilter.value || ''}
                                    onChange={(_, newValue) => setTempFilter({ ...tempFilter, value: newValue })}
                                    onKeyDown={(e) => { if (e.key === 'Enter') saveFilter(); }}
                                />
                            )}
                        </>
                    )}

                    {needsEndValue(tempFilter.operator) && (
                        <>
                            {isDateColumn(panelState.columnName) ? (
                                <DatePicker
                                    label="End Value"
                                    value={tempFilter.valueEnd ? new Date(tempFilter.valueEnd) : undefined}
                                    onSelectDate={(date) => setTempFilter({ ...tempFilter, valueEnd: date?.toISOString().split('T')[0] || '' })}
                                />
                            ) : (
                                <TextField
                                    label="End Value"
                                    value={tempFilter.valueEnd || ''}
                                    onChange={(_, newValue) => setTempFilter({ ...tempFilter, valueEnd: newValue })}
                                    onKeyDown={(e) => { if (e.key === 'Enter') saveFilter(); }}
                                />
                            )}
                        </>
                    )}

                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <PrimaryButton text="Apply Filter" onClick={saveFilter} />
                        <DefaultButton text="Remove Filter" onClick={removeFilter} />
                    </Stack>
                </Stack>
            </Panel>

            {/* Group By Panel */}
            <Panel
                isOpen={panelState.isOpen && panelState.type === 'groupby'}
                onDismiss={closePanel}
                type={PanelType.medium}
                headerText={`Group By: ${panelState.columnName}`}
            >
                <Stack tokens={{ childrenGap: 15 }} styles={{ root: { marginTop: 20 } }}>
                    {isDateColumn(panelState.columnName) && (
                        <>
                            <Text>Date Grouping Options:</Text>
                            <ChoiceGroup
                                options={dateGroupingOptions}
                                selectedKey={String(tempDateGrouping)}
                                onChange={(_, option) => setTempDateGrouping(parseInt(option?.key || '0'))}
                            />
                        </>
                    )}

                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <PrimaryButton text="Apply Group By" onClick={saveGroupBy} />
                        <DefaultButton text="Remove Group By" onClick={removeGroupBy} />
                    </Stack>
                </Stack>
            </Panel>

            {/* Aggregate Panel */}
            <Panel
                isOpen={panelState.isOpen && panelState.type === 'aggregate'}
                onDismiss={closePanel}
                type={PanelType.medium}
                headerText={`Aggregate: ${panelState.columnName}`}
            >
                <Stack tokens={{ childrenGap: 15 }} styles={{ root: { marginTop: 20 } }}>
                    <Dropdown
                        label="Aggregation Function"
                        placeholder="Select a function"
                        options={aggregationFunctionOptions}
                        selectedKey={tempAggregation.function}
                        onChange={(_, option) => {
                            const funcKey = option?.key as number;
                            setTempAggregation({ function: funcKey });
                            handleAggregationChange(funcKey);
                        }}
                    />

                    <Text variant="small" styles={{ root: { color: '#605e5c', fontStyle: 'italic' } }}>
                        Select a function to apply aggregation, or choose "None" to remove it.
                    </Text>
                </Stack>
            </Panel>
        </>
    );
};

export default ColumnListWithActions;
